/**
 * WatermarkUnlockPrompt — inline "🔒 Trial watermark · Remove →" pill
 * that opens an upgrade modal on click. Reusable across every surface
 * that displays trial-watermarked content (voice takes, images, etc.).
 *
 * Behavior:
 *  - Renders as a compact amber-outlined pill inline with the content.
 *  - Click opens a modal with an "aspirational preview" (blurred/faded
 *    version of the content labeled "Pro output") and the primary CTA
 *    "Upgrade to Pro — 20% off first month". Modal closes without
 *    charging on "Not now" / ESC / outside click.
 *  - Primary CTA hits POST /api/billing/checkout-subscription with
 *    promo_code=TRIAL3DAY20 and redirects to Stripe.
 *
 * Props:
 *  - kind: "audio" | "image" (drives modal copy + preview scaffold)
 *  - previewSrc?: URL of the trial output being previewed (image src
 *    or audio src). Used for the "actual output" side of the modal.
 *  - label?: short label of the content (e.g. voice-take name).
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Lock, X, Loader2, Sparkles } from "lucide-react";

const PROMO_CODE = "TRIAL3DAY20";

export default function WatermarkUnlockPrompt({ kind = "audio", previewSrc = "", label = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const upgrade = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout-subscription", {
        plan_id: "professional",
        cycle: "monthly",
        origin_url: window.location.origin,
        promo_code: PROMO_CODE,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Couldn't start checkout — try again");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed to start");
    } finally {
      setBusy(false);
    }
  };

  const kindCopy = kind === "image"
    ? {
        title: "Remove the image watermark",
        desc: "Your trial image includes a “Made with BroadcastFlow AI” tag in the corner. Upgrade to Pro for clean, unbranded images across every studio.",
        previewLabel: "Pro output — no watermark",
      }
    : {
        title: "Remove the audio watermark",
        desc: "Your trial voice take includes a short “Made with BroadcastFlow AI” sting at the end. Upgrade to Pro for tag-free audio across every studio.",
        previewLabel: "Pro output — clean audio",
      };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="watermark-unlock-pill"
        className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/50 bg-brand-amber/10 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-brand-amber hover:bg-brand-amber/20 transition"
        title="This output includes a trial watermark. Click to remove."
      >
        <Lock className="h-3 w-3" />
        Watermark · Remove →
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="watermark-unlock-modal-overlay"
        >
          <div
            className="relative bg-bg-surface border border-line rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="watermark-unlock-modal"
          >
            <button
              type="button"
              onClick={() => !busy && setOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-md text-ink-muted hover:text-ink-primary hover:bg-bg-elevated transition"
              aria-label="Close"
              data-testid="watermark-unlock-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="pr-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber mb-1">
                // TRIAL WATERMARK
              </div>
              <h2 className="font-display text-2xl font-bold text-ink-primary">
                {kindCopy.title}
              </h2>
              <p className="text-ink-secondary text-sm mt-1">
                {kindCopy.desc}
              </p>
              {label && (
                <p className="text-ink-muted text-xs mt-1 font-mono truncate">
                  {label}
                </p>
              )}
            </div>

            {/* Preview scaffold — shows aspirational "clean" side */}
            <div className="grid grid-cols-2 gap-3">
              {/* Left: actual trial output */}
              <div className="rounded-xl border border-line bg-bg-elevated overflow-hidden">
                <div className="px-3 py-2 border-b border-line font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                  Your trial output
                </div>
                <div className="aspect-square flex items-center justify-center bg-black/40 p-2">
                  {kind === "image" && previewSrc ? (
                    <img src={previewSrc} alt="Trial output" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center text-ink-secondary text-xs px-4">
                      <Lock className="h-6 w-6 mx-auto text-brand-amber/60 mb-2" />
                      Voice take with<br/>&ldquo;Made with BroadcastFlow AI&rdquo;<br/>sting at the end
                    </div>
                  )}
                </div>
              </div>
              {/* Right: aspirational Pro version — blurred/gated */}
              <div className="relative rounded-xl border border-brand-amber/40 bg-brand-amber/5 overflow-hidden">
                <div className="px-3 py-2 border-b border-brand-amber/30 font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                  {kindCopy.previewLabel}
                </div>
                <div className="aspect-square flex items-center justify-center bg-black/40 p-2 relative">
                  {kind === "image" && previewSrc ? (
                    <>
                      <img
                        src={previewSrc}
                        alt="Pro output preview"
                        className="max-w-full max-h-full object-contain blur-md scale-105 opacity-70"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <Lock className="h-8 w-8 mx-auto text-brand-amber mb-2" />
                          <div className="text-brand-amber text-xs font-mono uppercase tracking-wider">
                            Unlock with Pro
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <Sparkles className="h-8 w-8 mx-auto text-brand-amber mb-2" />
                      <div className="text-brand-amber text-xs font-mono uppercase tracking-wider">
                        Clean tag-free audio
                      </div>
                      <div className="text-ink-muted text-[10px] mt-1 font-mono">
                        Unlock with Pro
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Value bullets */}
            <ul className="grid grid-cols-2 gap-1.5 text-xs text-ink-secondary">
              <li className="flex items-start gap-1.5"><span className="text-brand-amber mt-0.5">✓</span> 1,500 AI generations / mo</li>
              <li className="flex items-start gap-1.5"><span className="text-brand-amber mt-0.5">✓</span> No watermark on any output</li>
              <li className="flex items-start gap-1.5"><span className="text-brand-amber mt-0.5">✓</span> Every studio unlocked</li>
              <li className="flex items-start gap-1.5"><span className="text-brand-amber mt-0.5">✓</span> Priority support</li>
            </ul>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <button
                type="button"
                onClick={upgrade}
                disabled={busy}
                data-testid="watermark-unlock-upgrade-btn"
                className="popular-pulse inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-60 flex-1"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {busy ? "Starting checkout…" : "Upgrade to Pro — 20% off first month"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid="watermark-unlock-notnow-btn"
                className="rounded-md border border-line bg-bg-elevated px-5 py-2.5 text-sm text-ink-secondary hover:text-ink-primary transition"
              >
                Not now
              </button>
            </div>

            <p className="text-[10px] text-ink-muted text-center pt-1 font-mono">
              Cancel anytime · Instant watermark removal on activation
            </p>
          </div>
        </div>
      )}
    </>
  );
}
